import { ContactUsFormSchema } from './contact-us-schema';
import { FormConfirmationLines } from './svg/form-confirmation-lines';
import { headlessPageDataAtom, parseSanitizeItem } from '@exploration/ui-utils';
import { MDSText } from '@thrivent/midwest-web';
import { useAtomValue } from 'jotai';
import Image from 'next/image';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { CSSTransition } from 'react-transition-group';
import styled from 'styled-components';

// --------------------------------------------------------------------------

const ConfirmationTitle = styled(MDSText.HeroMd)`
  &::first-letter {
    text-transform: capitalize;
  }
`;
const ConfirmationSubTitle = styled(MDSText.Subtitle)``;

const ConfirmationImageWrapper = styled.div`
  position: relative;
`;

const ConfirmationLines = styled(FormConfirmationLines)`
  position: absolute;
  z-index: 2;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;

  opacity: 0;
  transform: scale(0.9);
  @keyframes pop-in {
    0% {
      opacity: 0;
      transform: scale(0.9);
    }
    1% {
      opacity: 1;
      transform: scale(0.9);
    }
    99% {
      opacity: 1;
      transform: scale(1.1);
    }
    100% {
      opacity: 1;
      transform: scale(1.1);
    }
  }
  animation: pop-in 400ms ease-out 450ms;
  animation-fill-mode: forwards;

  line {
    stroke-dasharray: 8px;
    stroke-dashoffset: 0px;
    @keyframes shrink-line {
      0% {
        stroke-dashoffset: 0px;
      }
      100% {
        stroke-dashoffset: 8px;
      }
    }
    @media (prefers-reduced-motion: no-preference) {
      animation: shrink-line 300ms ease-out 500ms;
      animation-fill-mode: forwards;
    }
  }
`;

const ConfirmationImage = styled(Image)`
  display: block;
  margin-top: 1rem;
  position: relative;
  z-index: 1;

  transform: translateY(0);
  @keyframes hand-bounce {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-20px);
    }
    60% {
      transform: translateY(-10px);
    }
  }
  @media (prefers-reduced-motion: no-preference) {
    animation: hand-bounce 1400ms ease;
  }
`;

const ConfirmationDescription = styled(MDSText.Subtitle)`
  max-width: 22rem;
  margin-top: 1rem;
`;

const ConfirmationContainer = styled.div`
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding-top: 1rem;
  padding-bottom: 1rem;

  @media (prefers-reduced-motion: no-preference) {
    &.my-node-enter {
      opacity: 0;
    }
    &.my-node-enter-active {
      opacity: 1;
      transition: opacity 500ms;
    }
  }
`;

// --------------------------------------------------------------------------

export const FormConfirmation = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const form = useFormContext<ContactUsFormSchema>();
  const pageData = useAtomValue(headlessPageDataAtom);
  const { text, image } = pageData ?? {};
  const confirmationImage = image?.['confirmation-success'];

  useEffect(() => {
    // Move us up to the confirmation if the user scrolled down when filling out the form
    scrollRef?.current?.scrollIntoView({ block: 'center' });
  }, []);

  const firstName = form.getValues('r_First_Name');
  const firstNameText = firstName ? firstName + ', ' : '';

  return (
    <CSSTransition
      nodeRef={scrollRef}
      in={true}
      timeout={400}
      classNames="my-node"
    >
      <ConfirmationContainer ref={scrollRef}>
        <ConfirmationTitle as="h3">
          <span className="fs-mask dd-privacy-mask">
            {parseSanitizeItem(firstNameText)}
          </span>
          {parseSanitizeItem(text?.['confirmation-title'])}
        </ConfirmationTitle>
        <ConfirmationSubTitle as="p">
          {parseSanitizeItem(text?.['confirmation-subtitle'])}
        </ConfirmationSubTitle>
        {confirmationImage && (
          <ConfirmationImageWrapper>
            <ConfirmationLines />
            {/** Unoptimized, so we can preload it in other components. (Stops it from "Flickering in") */}
            <ConfirmationImage unoptimized {...confirmationImage} />{' '}
          </ConfirmationImageWrapper>
        )}
        <ConfirmationDescription as="p">
          {parseSanitizeItem(text?.['confirmation-description'])}
        </ConfirmationDescription>
      </ConfirmationContainer>
    </CSSTransition>
  );
};
